import { BadgeScanSelection, badgeScanSelections, FormatColor } from './types';
import { green, orange, red } from '@mui/material/colors';
import { TFunction } from 'i18next';
import { Sorting } from '@devexpress/dx-react-grid';
import { TableFilterRow } from '@devexpress/dx-react-grid-material-ui';
import { styled } from '@mui/material/styles';

/**
 * Gets the css color for a conditional format color. Returns undefined if None.
 * @param color
 */
export const conditionalFormatColorToColor = (
  color: FormatColor | undefined,
): string | undefined => {
  switch (color) {
    case 'Green':
      return green[500];
    case 'Yellow':
      return orange[500];
    case 'Red':
      return red[500];
    default:
      return undefined;
  }
};

/**
 * Gets the css contrast color for a condition format color.
 * @param formatColor
 */
export const conditionalFormatContrastColor = (
  formatColor: FormatColor | undefined,
): string => {
  return formatColor === 'Red' || formatColor === 'Green' ? 'white' : 'inherit';
};

/**
 * Converts a badge scan selection to it corresponding string key
 * @param badgeScanSelection
 */
export const badgeScanSelectionToStringKey = (
  badgeScanSelection: BadgeScanSelection,
) => {
  switch (badgeScanSelection) {
    case 'badgeId':
      return 'badgeId';
    case 'employeeId':
      return 'employeeId';
  }
};

export type ScanSelection = {
  label: string;
  value: BadgeScanSelection;
};

/**
 * Gets options for Badge Scan selections
 * @param t
 */
export const getBadgeScanSelections = (t: TFunction): ScanSelection[] => {
  return badgeScanSelections.map((x) => ({
    label: t(badgeScanSelectionToStringKey(x)),
    value: x,
  }));
};

/**
 * Url of sharepoint document that contains help document .
 */
export const TABLE_HELP_URL =
  'https://marvinonline.sharepoint.com/:w:/s/RapidSolutionsTeam-Warroad/EUqc0xwZpRpGsNaMINEb3bQBZGrtRCh9NZOhcbkO74pNtw';

/**
 * Sorts an array of data objects based on a specified sorting criteria.
 *
 * This generic function is designed to sort any array of objects (`data`) according to
 * one or more sorting conditions provided in the `sorting` array. Each sorting condition
 * specifies the column name to sort by and the direction (ascending or descending).
 * The `getColumnValue` function is used to extract the value from a data object based on
 * the column name, allowing for custom value retrieval logic (e.g., handling nested properties).
 *
 * The function supports sorting by strings, numbers, and booleans. String comparisons are
 * performed using locale-sensitive comparison, numbers are sorted numerically, and booleans
 * are converted to numbers for comparison. If the `sorting` array contains multiple conditions,
 * the data is sorted in the order those conditions are specified, similar to SQL ORDER BY clause.
 *
 * @template TDataSource The data type of the objects in the data array. It must be an object type.
 * @param {TDataSource[]} data The array of data objects to sort.
 * @param {Sorting[]} sorting An array of sorting conditions, where each condition specifies
 *        the column name (`columnName`) and the direction (`asc` for ascending, `desc` for descending).
 * @param {(row: TDataSource, columnName: string) => any} getColumnValue A function that retrieves
 *        the value from a data object for a given column name. This allows for custom logic in value retrieval.
 * @returns {TDataSource[]} A new array containing the sorted data objects. The original data array is not mutated.
 */
export const sortData = <TDataSource extends object>(
  data: TDataSource[],
  sorting: Sorting[],
  getColumnValue: (row: TDataSource, columnName: string) => any,
): TDataSource[] => {
  const sortedData = [...data];
  const compareRows = (a: TDataSource, b: TDataSource) => {
    for (const sort of sorting) {
      const { columnName, direction } = sort;

      const valueA = getColumnValue(a, columnName);
      const valueB = getColumnValue(b, columnName);

      let comparisonResult = 0;
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        comparisonResult = valueA.localeCompare(valueB);
      } else if (typeof valueA == 'number' && typeof valueB === 'number') {
        comparisonResult = valueA - valueB;
      } else if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
        comparisonResult = Number(valueA) - Number(valueB);
      }

      if (comparisonResult !== 0) {
        return direction === 'asc' ? comparisonResult : -comparisonResult;
      }
    }

    return 0;
  };
  sortedData.sort(compareRows);
  return sortedData;
};

/**
 * Styled component for TableFilterRow Cell
 */
export const StyledTableFilterRowCell = styled(TableFilterRow.Cell)({
  height: '46.84px',
});

/**
 * Client-side CSV builder for `GridTableDefault2`. Does not read React `cell` renderers; values come from
 * accessors and optional export hooks (see `GenerateCsvFromMrtOptions` and `GridTableDefault2ExportColumnMeta`).
 */
import type { MRT_ColumnDef, MRT_RowData } from 'material-react-table';
import { stringifyCsvCellValue } from '../../utils/string-utils';
import type { AdditionalExportColumn } from './types';

/** Optional `meta` keys on `MRT_ColumnDef` used only when generating CSV (see `GridTableDefault2` docs). */
export type GridTableDefault2ExportColumnMeta<TData extends MRT_RowData> = {
  /** Per-column export value; wins over table-level `onExportGetCellValue` and accessors. */
  exportCellValue?: (row: TData) => unknown;
};

export type GenerateCsvFromMrtOptions<TData extends MRT_RowData> = {
  /**
   * Table-level override for how a cell is resolved for CSV (same role as legacy `GridTableDefault`’s
   * `onExportGetCellValue` when using the default exporter path). Return `undefined` to use the column accessor.
   *
   * **Not used** when `additionalExportColumns` is non-empty: that merge path uses accessors only for MRT columns,
   * matching the legacy `generateCsvContent` behavior.
   */
  onExportGetCellValue?: (row: TData, columnId: string) => unknown;
  /**
   * Export-only columns (see `AdditionalExportColumn`). When provided, each is inserted at `position` into the
   * merged header row; base MRT columns use accessors only for that run.
   */
  additionalExportColumns?: AdditionalExportColumn[];
};

function getColumnId(col: { id?: string; accessorKey?: string }): string {
  return (col.id ?? col.accessorKey ?? '') as string;
}

function getValueByAccessorKey<TData extends MRT_RowData>(
  row: TData,
  accessorKey: string | number | symbol | undefined,
): unknown {
  if (accessorKey == null) return undefined;
  if (typeof accessorKey !== 'string') {
    return (row as Record<string | number | symbol, unknown>)[accessorKey];
  }
  const parts = accessorKey.split('.');
  let cur: unknown = row;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

function getExportHeader<TData extends MRT_RowData>(
  col: MRT_ColumnDef<TData>,
): string {
  const id = getColumnId(col);
  const h = col.header;
  if (typeof h === 'string') return h;
  return id;
}

/** Legacy “additional columns” CSV path: no `meta.exportCellValue` / `onExportGetCellValue`. */
function getExportCellValueAccessorOnly<TData extends MRT_RowData>(
  col: MRT_ColumnDef<TData>,
  row: TData,
): unknown {
  if (col.accessorFn) {
    return col.accessorFn(row);
  }
  return getValueByAccessorKey(row, col.accessorKey as string | undefined);
}

function getExportCellValueFull<TData extends MRT_RowData>(
  col: MRT_ColumnDef<TData>,
  row: TData,
  options?: GenerateCsvFromMrtOptions<TData>,
): unknown {
  const meta = col.meta as GridTableDefault2ExportColumnMeta<TData> | undefined;
  if (typeof meta?.exportCellValue === 'function') {
    return meta.exportCellValue(row);
  }
  if (options?.onExportGetCellValue) {
    const v = options.onExportGetCellValue(row, getColumnId(col));
    if (v !== undefined) return v;
  }
  return getExportCellValueAccessorOnly(col, row);
}

function getAdditionalExportHeader(col: AdditionalExportColumn): string {
  return col.title ?? col.name;
}

function getAdditionalExportCellValue<TData extends MRT_RowData>(
  row: TData,
  col: AdditionalExportColumn,
): unknown {
  if (col.getCellValue) {
    return col.getCellValue(row, col.name);
  }
  return (row as Record<string, unknown>)[col.name];
}

type MrtOrAdditional<TData extends MRT_RowData> =
  | { kind: 'mrt'; column: MRT_ColumnDef<TData> }
  | { kind: 'additional'; column: AdditionalExportColumn };

/**
 * Interleaves `additionalExportColumns` into the MRT column list. Sorts extras by `position` first, then splices
 * each at `column.position` in order (same algorithm as legacy `GridTableDefault`).
 */
function mergeExportColumns<TData extends MRT_RowData>(
  columns: MRT_ColumnDef<TData>[],
  additionalExportColumns?: AdditionalExportColumn[],
): MrtOrAdditional<TData>[] {
  const parts: MrtOrAdditional<TData>[] = columns.map((column) => ({
    kind: 'mrt',
    column,
  }));
  if (!additionalExportColumns?.length) {
    return parts;
  }
  const sorted = [...additionalExportColumns].sort(
    (a, b) => a.position - b.position,
  );
  for (const column of sorted) {
    parts.splice(column.position, 0, { kind: 'additional', column });
  }
  return parts;
}

/**
 * Builds CSV text from MRT column defs and row data.
 *
 * - **Default path:** `meta.exportCellValue` and `onExportGetCellValue` apply (see options).
 * - **With `additionalExportColumns`:** matches legacy merged export — MRT columns use accessors only; each
 *   additional column uses `getCellValue` or `row[name]`.
 */
export function generateCsvContentFromMrtColumnDefs<TData extends MRT_RowData>(
  columns: MRT_ColumnDef<TData>[],
  rows: TData[],
  options?: GenerateCsvFromMrtOptions<TData>,
): string {
  const hasAdditional = !!options?.additionalExportColumns?.length;
  const merged = mergeExportColumns(columns, options?.additionalExportColumns);

  const exportParts = merged.filter((p) =>
    p.kind === 'mrt' ? getColumnId(p.column) !== '' : true,
  );

  const headers = exportParts.map((p) =>
    stringifyCsvCellValue(
      p.kind === 'mrt'
        ? getExportHeader(p.column)
        : getAdditionalExportHeader(p.column),
    ),
  );
  const csvRows = [headers.join(',')];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const values = exportParts.map((p) => {
      let cell: unknown;
      if (p.kind === 'mrt') {
        cell = hasAdditional
          ? getExportCellValueAccessorOnly(p.column, row)
          : getExportCellValueFull(p.column, row, options);
      } else {
        cell = getAdditionalExportCellValue(row, p.column);
      }
      return stringifyCsvCellValue(cell);
    });
    csvRows.push(values.join(','));
  }
  return csvRows.join('\n');
}
